import React, { useMemo, useState, useEffect, useRef } from 'react';
import { Bar, Chart } from 'react-chartjs-2';
import { Chart as ChartJS } from 'chart.js';
import { useApp } from '../../contexts/AppContext';
import { calculateSingleProperty, formatValue } from '../../utils';

const Wasserfall: React.FC = () => {
  const { currentInputs, projectionDurationYears } = useApp();
  const [selectedYear, setSelectedYear] = useState(1);
  const chartRefs = useRef<{ [key: string]: ChartJS | null }>({});

  // Cleanup chart instances on unmount
  useEffect(() => {
    // Capture refs for cleanup
    const charts = chartRefs.current;
    
    return () => {
      Object.values(charts).forEach(chart => {
        if (chart) {
          chart.destroy();
        }
      });
    };
  }, []);
  
  const calculations = useMemo(() => {
    return calculateSingleProperty(currentInputs);
  }, [currentInputs]);

  const yearData = calculations?.projectionData?.[selectedYear];

  // Cumulative Waterfall Data - showing progression over years
  const cumulativeData = useMemo(() => {
    if (!calculations?.projectionData) return [];
    
    const data = [];
    let cumulative = 0;
    
    for (let i = 1; i <= Math.min(projectionDurationYears, 30); i++) {
      const yearData = calculations.projectionData[i];
      if (yearData) {
        cumulative += yearData.cashflow;
        data.push({
          year: i,
          annual: yearData.cashflow,
          cumulative: cumulative,
          mieteinnahmen: yearData.mieteinnahmenJahr || 0,
          kosten: (yearData.hausgeldJahr || 0) + (yearData.instandhaltungJahr || 0) + (yearData.zinsJahr || 0),
          steuern: yearData.steuerJahr || 0
        });
      }
    }
    return data;
  }, [calculations, projectionDurationYears]);

  if (!calculations || !calculations.projectionData || !yearData) {
    return null;
  }

  // Annual Waterfall Data
  const annualWaterfallData = {
    labels: [
      'Mieteinnahmen',
      'Hausgeld',
      'Instandhaltung',
      'Zinskosten',
      'AfA',
      'Steuern',
      'Netto Cashflow'
    ],
    datasets: [{
      label: 'Cashflow Komponenten',
      data: [
        yearData.mieteinnahmenJahr || 0,
        -(yearData.hausgeldJahr || 0),
        -(yearData.instandhaltungJahr || 0),
        -(yearData.zinsJahr || 0),
        yearData.afaJahr || 0, // AfA is positive as it reduces taxes
        -(yearData.steuerJahr || 0),
        yearData.cashflow
      ],
      backgroundColor: [
        'rgba(34, 197, 94, 0.8)',  // Green for income
        'rgba(239, 68, 68, 0.8)',   // Red for expenses
        'rgba(239, 68, 68, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(59, 130, 246, 0.8)',  // Blue for tax benefit
        (yearData.steuerJahr || 0) > 0 ? 'rgba(239, 68, 68, 0.8)' : 'rgba(34, 197, 94, 0.8)',
        yearData.cashflow > 0 ? 'rgba(34, 197, 94, 0.8)' : 'rgba(239, 68, 68, 0.8)'
      ],
      borderColor: [
        'rgb(34, 197, 94)',
        'rgb(239, 68, 68)',
        'rgb(239, 68, 68)',
        'rgb(239, 68, 68)',
        'rgb(59, 130, 246)',
        (yearData.steuerJahr || 0) > 0 ? 'rgb(239, 68, 68)' : 'rgb(34, 197, 94)',
        yearData.cashflow > 0 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'
      ],
      borderWidth: 1
    }]
  };

  const cumulativeWaterfallData = {
    labels: cumulativeData.map(d => `Jahr ${d.year}`),
    datasets: [
      {
        label: 'Jährlicher Cashflow',
        data: cumulativeData.map(d => d.annual),
        type: 'bar' as const,
        backgroundColor: cumulativeData.map(d => 
          d.annual > 0 ? 'rgba(34, 197, 94, 0.5)' : 'rgba(239, 68, 68, 0.5)'
        ),
        borderColor: cumulativeData.map(d => 
          d.annual > 0 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'
        ),
        borderWidth: 1,
        yAxisID: 'y'
      },
      {
        label: 'Kumulierter Cashflow',
        data: cumulativeData.map(d => d.cumulative),
        type: 'line' as const,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        yAxisID: 'y1'
      }
    ]
  };

  // Component breakdown over time
  const componentBreakdownData = {
    labels: cumulativeData.slice(0, 20).map(d => `Jahr ${d.year}`),
    datasets: [
      {
        label: 'Mieteinnahmen',
        data: cumulativeData.slice(0, 20).map(d => d.mieteinnahmen),
        backgroundColor: 'rgba(34, 197, 94, 0.8)',
        borderColor: 'rgb(34, 197, 94)',
        borderWidth: 1
      },
      {
        label: 'Kosten',
        data: cumulativeData.slice(0, 20).map(d => -d.kosten),
        backgroundColor: 'rgba(239, 68, 68, 0.8)',
        borderColor: 'rgb(239, 68, 68)',
        borderWidth: 1
      },
      {
        label: 'Steuern',
        data: cumulativeData.slice(0, 20).map(d => -d.steuern),
        backgroundColor: 'rgba(251, 191, 36, 0.8)',
        borderColor: 'rgb(251, 191, 36)',
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            const value = context.parsed.y;
            label += formatValue(value, '€');
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Betrag (€)'
        },
        ticks: {
          callback: (value: any) => formatValue(value, '€')
        }
      }
    }
  };

  const cumulativeChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            const value = context.parsed.y;
            label += formatValue(value, '€');
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Jährlicher Cashflow (€)'
        },
        ticks: {
          callback: (value: any) => formatValue(value, '€')
        }
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: true,
          text: 'Kumulierter Cashflow (€)'
        },
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          callback: (value: any) => formatValue(value, '€')
        }
      }
    }
  };

  // Summary metrics
  const summaryMetrics = {
    totalMieteinnahmen: cumulativeData.reduce((sum, d) => sum + d.mieteinnahmen, 0),
    totalKosten: cumulativeData.reduce((sum, d) => sum + d.kosten, 0),
    totalSteuern: cumulativeData.reduce((sum, d) => sum + d.steuern, 0),
    totalCashflow: cumulativeData[cumulativeData.length - 1]?.cumulative || 0,
    breakEvenYear: cumulativeData.findIndex(d => d.cumulative > 0) + 1
  };

  return (
    <div className="tab-content active space-y-6">
      <h2 className="text-2xl font-bold mb-6">Wasserfall Analyse</h2>

      {/* Year Selector */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Jahr für detaillierte Analyse auswählen:
        </label>
        <div className="flex items-center gap-4">
          <input
            type="range"
            min="1"
            max={Math.min(projectionDurationYears, 30)}
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="flex-1"
          />
          <span className="text-lg font-semibold w-20">Jahr {selectedYear}</span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Gesamte Mieteinnahmen</div>
          <div className="text-xl font-bold text-green-600">
            {formatValue(summaryMetrics.totalMieteinnahmen, '€')}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Gesamte Kosten</div>
          <div className="text-xl font-bold text-red-600">
            {formatValue(summaryMetrics.totalKosten, '€')}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Gesamte Steuern</div>
          <div className="text-xl font-bold text-orange-600">
            {formatValue(summaryMetrics.totalSteuern, '€')}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Kumulierter Cashflow</div>
          <div className={`text-xl font-bold ${summaryMetrics.totalCashflow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatValue(summaryMetrics.totalCashflow, '€')}
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-sm text-gray-600">Break-Even Jahr</div>
          <div className="text-xl font-bold text-blue-600">
            {summaryMetrics.breakEvenYear > 0 ? `Jahr ${summaryMetrics.breakEvenYear}` : 'N/A'}
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Annual Waterfall */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">
            Cashflow Wasserfall - Jahr {selectedYear}
          </h3>
          <div style={{ height: '400px' }}>
            <Bar 
              data={annualWaterfallData} 
              options={chartOptions}
              ref={(ref) => {
                if (ref) {
                  chartRefs.current['annualWaterfall'] = ref as unknown as ChartJS;
                }
              }}
            />
          </div>
          <div className="mt-4 text-sm text-gray-600">
            <p>Monatlicher Cashflow: {formatValue(yearData.cashflow / 12, '€')}</p>
            <p>Jährlicher Cashflow: {formatValue(yearData.cashflow, '€')}</p>
          </div>
        </div>

        {/* Component Breakdown */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold mb-4">Komponenten über Zeit (20 Jahre)</h3>
          <div style={{ height: '400px' }}>
            <Bar 
              data={componentBreakdownData}
              ref={(ref) => {
                if (ref) {
                  chartRefs.current['componentBreakdown'] = ref as unknown as ChartJS;
                }
              }}
              options={{
              ...chartOptions,
              scales: {
                ...chartOptions.scales,
                x: {
                  stacked: true
                },
                y: {
                  ...chartOptions.scales.y,
                  stacked: true
                }
              }
            }} />
          </div>
        </div>

        {/* Cumulative Waterfall */}
        <div className="bg-white p-6 rounded-lg shadow-md lg:col-span-2">
          <h3 className="text-lg font-semibold mb-4">
            Kumulierter Cashflow Verlauf
          </h3>
          <div style={{ height: '400px' }}>
            <Chart 
              type='bar' 
              data={cumulativeWaterfallData} 
              options={cumulativeChartOptions}
              ref={(ref) => {
                if (ref) {
                  chartRefs.current['cumulativeWaterfall'] = ref as unknown as ChartJS;
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Detailed Year Table */}
      <div className="bg-white p-6 rounded-lg shadow-md overflow-x-auto">
        <h3 className="text-lg font-semibold mb-4">Detaillierte Jahresübersicht</h3>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Jahr</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mieteinnahmen</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hausgeld</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Instandhaltung</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zinsen</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Steuern</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Netto CF</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kumuliert</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {calculations.projectionData.slice(1, Math.min(21, projectionDurationYears + 1)).map(year => (
              <tr key={year.jahr} className={year.jahr === selectedYear ? 'bg-blue-50' : ''}>
                <td className="px-4 py-2 whitespace-nowrap text-sm font-medium">{year.jahr}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-green-600">
                  {formatValue(year.mieteinnahmenJahr || 0, '€')}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-red-600">
                  {formatValue(year.hausgeldJahr || 0, '€')}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-red-600">
                  {formatValue(year.instandhaltungJahr || 0, '€')}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-red-600">
                  {formatValue(year.zinsJahr || 0, '€')}
                </td>
                <td className={`px-4 py-2 whitespace-nowrap text-sm ${(year.steuerJahr || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatValue(year.steuerJahr || 0, '€')}
                </td>
                <td className={`px-4 py-2 whitespace-nowrap text-sm font-semibold ${year.cashflow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatValue(year.cashflow, '€')}
                </td>
                <td className={`px-4 py-2 whitespace-nowrap text-sm font-semibold ${(year.cumulativeCashflowNachSteuer || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatValue(year.cumulativeCashflowNachSteuer || 0, '€')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Wasserfall;